import { Autocomplete, TextField } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import { InwardType } from "Constants/inwardTypes";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";
import { Axios } from "api/axios";
import CancelBackButton from "components/Buttons/CancelBackButton";
import SubmitButton from "components/Buttons/SubmitButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "components/PageContainer/PageContainer";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import { resetGRNState } from "slices/Warehouse/ItemInward2/GRN/reducer";
import {
  fetchGRNDetails,
  updateOrderReturnGRN,
} from "slices/Warehouse/ItemInward2/GRN/thunk";
import { showConfirm, showError, showValidationErrors } from "utils/swalHelper";
import * as Yup from "yup";

interface GRNFormValues {
  inward_date: string;
  warehouse: string;
  warehouse_id: string;
  upload_document: File | null;
  existing_document_file: string | null; // Store existing document URL for edit mode
  remark: string;
  received_by: string;
  received_by_user_id: string;
  grn_items: any[];
}

const EditSalesOrderReturnGNR: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const dispatch = useDispatch<any>();

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  // Redux state
  const { loading: grnLoading } = useSelector((state: any) => state.grn);

  // Check if we're in edit mode (ID in URL params)
  const inventoryId = params.id;
  const isEdit = Boolean(inventoryId);

  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  const [grnData, setGrnData] = useState<any>({});
  const validationSchema = Yup.object({
    inward_date: Yup.string().required("Please select Inward Date"),
    warehouse: Yup.string().required("Please select Warehouse"),
    remark: Yup.string(),
    upload_document: Yup.mixed().nullable(),
    received_by_user_id: Yup.string().required("Please select Received By"),
  });

  // Get today's date in YYYY-MM-DD format for date input
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const formik = useFormik<GRNFormValues>({
    initialValues: {
      inward_date: isEdit ? "" : getTodayDate(), // Auto-select today's date in add mode
      warehouse: "",
      warehouse_id: "",
      upload_document: null as File | null,
      existing_document_file: null as string | null,
      remark: "",
      received_by: "",
      received_by_user_id: "",
      grn_items: [],
    },

    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Reset any previous errors
      dispatch(resetGRNState());

      // ✅ Validate items
      if (!values.grn_items || values.grn_items.length === 0) {
        showError("Please add at least one item");
        return;
      }

      // ✅ Validate quantity
      const invalidQuantity = values.grn_items.some((item, index) => {
        const qty = item.return_quantity || item.quantity || 0;
        if (qty === undefined || qty === null || qty <= 0) {
          showError(
            `Return quantity must be greater than 0 for row ${index + 1}`
          );
          return true;
        }
        return false;
      });

      if (invalidQuantity) return;

      // ✅ Validate return_quantity cannot be greater than dispatch_quantity
      const invalidReturnQuantity = values.grn_items.some((item, index) => {
        const returnQty = Number(item.return_quantity || item.quantity || 0);
        const dispatchQty = Number(item.dispatch_quantity || 0);
        if (returnQty > dispatchQty) {
          showError(
            `Return quantity cannot be greater than dispatched quantity (${dispatchQty}) for row ${
              index + 1
            }`
          );
          return true;
        }
        return false;
      });

      if (invalidReturnQuantity) return;

      // ✅ Validate rate
      const invalidRate = values.grn_items.some((item, index) => {
        const rate = item.rate || 0;
        if (rate === undefined || rate === null || rate <= 0) {
          showError(`Rate must be greater than 0 for row ${index + 1}`);
          return true;
        }
        return false;
      });

      if (invalidRate) return;

      // ✅ Build FormData
      const formData = buildFormData(values, values.grn_items);

      try {
        if (isEdit && inventoryId) {
          // Update GRN
          await dispatch(
            updateOrderReturnGRN({
              inventory_id: inventoryId,
              payload: formData,
            })
          ).unwrap();
          toast.success("GRN updated successfully");
        }
        navigate("/warehouse-item-inward");
      } catch (err: any) {
        handleSubmitError(err);
      }
    },
  });

  useEffect(() => {
    const fetchWarehouseOptions = async () => {
      try {
        const response = await dispatch(fetchWarehouseDropdown()).unwrap();
        console.log("Warehouse Options:", response);
        setWarehouseOptions(response?.data || []);
      } catch (error) {
        console.error("Error fetching warehouse options:", error);
      }
    };
    fetchWarehouseOptions();
  }, [dispatch]);

  useEffect(() => {
    const fetchUserOptions = async () => {
      try {
        const response = await Axios.get("/users/dropdown/all");
        console.log("User Options:", response);
        setUserOptions(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching user options:", error);
        setUserOptions([]);
      }
    };
    fetchUserOptions();
  }, []);

  // Helper function to allow only numbers
  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (value === "" || regex.test(value)) {
      return value;
    }
    return false;
  };

  // Handler to update grn_items array fields
  const handleItemFieldChange = (index: number, field: string, value: any) => {
    const updatedItems = [...(formik.values.grn_items || [])];
    if (field === "return_quantity" || field === "quantity") {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        return_quantity: value, // Keep both for compatibility
        amount: parseFloat(updatedItems[index]?.rate || 0) * Number(value || 0),
      };
    } else if (field === "rate") {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        amount:
          parseFloat(
            updatedItems[index]?.return_quantity ||
              updatedItems[index]?.quantity ||
              0
          ) * Number(value),
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };
    }
    formik.setFieldValue("grn_items", updatedItems);
  };

  // Helper function to build FormData
  const buildFormData = useCallback(
    (values: GRNFormValues, items: any[]): FormData => {
      const formData = new FormData();
      formData.append("inward_date", values.inward_date);
      formData.append("remark", values.remark || "");
      formData.append("warehouse_id", values.warehouse_id || "");
      formData.append("inward_type", InwardType?.ORDER_RETURN);
      formData.append("received_by", values.received_by_user_id || "");

      // Append file if it exists (must be File object, not string)
      if (values.upload_document && values.upload_document instanceof File) {
        formData.append("document_file", values.upload_document);
      } else if (values.existing_document_file && !values.upload_document) {
        // If no new file is uploaded, send the existing document URL
        formData.append("document_file", values.existing_document_file);
      }

      console.log("GRN Items before transform: ", items);
      // Transform items to GRNItem format
      const transformedItems = items.map((row: any) => {
        const quantity = Number(row.return_quantity || row.quantity) || 0;
        const rate = Number(row.rate) || 0;

        return {
          master_item_id:
            row.master_item_id || row.item_details?.item_id || row.item_id,
          selling_price: rate,
          quantity: quantity,
          reserved_quantity: 0,
          available_quantity: quantity,
          remark: row.remarks || row.remark || null,
          warehouse_id: values.warehouse_id || "",
          amount: row.amount,
          issued_quantity: row.issued_quantity,
          dispatch_quantity: row.dispatch_quantity,
          order_quantity: row.order_quantity,
          item_details: row.item_details || {},
        };
      });

      formData.append("grn_items", JSON.stringify(transformedItems));
      return formData;
    },
    []
  );

  // Helper function to handle submit errors
  const handleSubmitError = useCallback((err: any) => {
    const responseData = err?.response?.data || err;
    const validationErrors = responseData?.data?.errors || responseData?.errors;

    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      try {
        showValidationErrors(validationErrors);
      } catch (swalError) {
        toast.error(validationErrors.join("; "));
      }
    } else {
      const errorMessage =
        responseData?.error ||
        responseData?.message ||
        "Failed to process GRN. Please try again.";
      toast.error(errorMessage);
    }
  }, []);

  // Fetch GRN data if in edit mode
  useEffect(() => {
    const fetchGRNData = async () => {
      if (!isEdit || !inventoryId || initialDataLoaded) return;

      setIsLoadingInitialData(true);
      try {
        const grnData = await dispatch(fetchGRNDetails(inventoryId)).unwrap();

        if (grnData) {
          setGrnData(grnData || {});
          // Populate form fields
          formik.setFieldValue(
            "inward_date",
            grnData.inward_date
              ? new Date(grnData.inward_date).toISOString().split("T")[0]
              : ""
          );
          formik.setFieldValue("remark", grnData.remarks || "");

          // Use warehouse_details JSON if available, fallback to joined warehouse relation
          const warehouseData = grnData.warehouse_details || grnData.warehouse;
          formik.setFieldValue(
            "warehouse_id",
            warehouseData?.warehouse_id || ""
          );
          formik.setFieldValue(
            "warehouse",
            warehouseData?.warehouse_name || ""
          );

          formik.setFieldValue(
            "existing_document_file",
            grnData.document_file || null
          );

          // Use received_by_details JSON if available, fallback to joined received_by_user relation
          const receivedByData =
            grnData.received_by_details || grnData.received_by_user;
          if (receivedByData) {
            formik.setFieldValue(
              "received_by_user_id",
              receivedByData.user_id || ""
            );
            const fullName =
              receivedByData.first_name && receivedByData.last_name
                ? `${receivedByData.first_name} ${receivedByData.last_name}`
                : receivedByData.username || "";
            formik.setFieldValue("received_by", fullName);
          }

          // Transform inventory_items to match table format
          if (grnData.inventory_items && grnData.inventory_items.length > 0) {
            const transformedItems = grnData.inventory_items.map(
              (invItem: any, index: number) => {
                const quantity = invItem.quantity || 0;
                const rate = invItem.selling_price || 0;
                const amount = quantity * rate;

                return {
                  master_item_id: invItem.master_item?.item_id,
                  item_details: {
                    item_id: invItem.master_item?.item_id,
                    item_name: invItem.master_item?.item_name,
                    item_code: invItem.master_item?.item_code,
                  },
                  category_name: invItem.master_item?.category?.category_name,
                  sub_category_name:
                    invItem.master_item?.sub_category?.sub_category_name,
                  unit_name: invItem.master_item?.unit?.unit_name || "",
                  order_quantity: invItem.issued_quantity || quantity,
                  dispatch_quantity: invItem.issued_quantity || quantity,
                  issued_quantity: invItem.issued_quantity || quantity,
                  return_quantity: quantity,
                  quantity: quantity,
                  rate: rate,
                  amount: amount,
                  remarks: invItem.remark || "",
                };
              }
            );

            formik.setFieldValue("grn_items", transformedItems);
          }

          setInitialDataLoaded(true);
        }
      } catch (error: any) {
        console.error("Error fetching GRN data:", error);
        toast.error(error?.message || "Failed to load GRN data");
        navigate("/warehouse-item-inward");
      } finally {
        setIsLoadingInitialData(false);
      }
    };

    fetchGRNData();
  }, [isEdit, inventoryId, dispatch, navigate, initialDataLoaded, formik]);

  console.log("Updated GRN Items: ", grnData);

  return (
    <PageContainer>
      <FormHeader
        title={isEdit ? "Edit GRN" : "Create GRN"}
        firstLabel="GRN List"
        secondLabel={isEdit ? "Edit GRN" : "Create GRN"}
        handleArrowClick={() => navigate("/warehouse-item-inward")}
      />

      {isLoadingInitialData ? (
        <LoadingSpinner />
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Inward Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="inward_date"
                  value={formik.values.inward_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.inward_date && formik.touched.inward_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.inward_date}
                </Form.Control.Feedback>
              </div>
            </Col>
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Customer Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="customer"
                  placeholder="Customer Name"
                  value={grnData?.supplier_details?.customer_name}
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched("customer", true)}
                  readOnly
                />
                <Form.Control.Feedback type="invalid">
                  {/* {typeof formik.errors.customer === "string" ? formik.errors.customer : "Please enter Customer Name"} */}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Order No. <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="order_no"
                  placeholder="Order Number"
                  value={grnData?.order?.order_no}

                  // isInvalid={
                  //   !!formik.errors.order_no && formik.touched.order_no
                  // }
                />
                <Form.Control.Feedback type="invalid">
                  {/* {formik.errors.order_no} */}
                </Form.Control.Feedback>
              </div>
            </Col>
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Dispatch No. <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="dispatch_no"
                  placeholder="Dispatch Number"
                  value={grnData?.dispatch?.dispatch_number}

                  // isInvalid={
                  //   !!formik.errors.dispatch_no && formik.touched.dispatch_no
                  // }
                />
                <Form.Control.Feedback type="invalid">
                  {/* {formik.errors.dispatch_no} */}
                </Form.Control.Feedback>
              </div>
            </Col>

            {/* <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Warehouse <span className="text-danger">*</span>
                </Form.Label>
                <WarehouseAutocomplete
                  options={warehouseOptions}
                  value={formik.values.warehouse}
                  valueType="warehouse_name"
                  formik={formik}
                  formikFieldId="warehouse_id"
                  formikFieldName="warehouse"
                  name="warehouse"
                  inputSx={{
                    background: "#FFF",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                  customSx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                  onChange={(warehouseId, warehouseName) => {
                    formik.setFieldValue("warehouse_id", warehouseId);
                    formik.setFieldValue("warehouse", warehouseName);
                    // Clear error if a valid warehouse is selected
                    if (warehouseName) {
                      formik.setFieldError("warehouse", undefined);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("warehouse", true)}
                />
              </div>
            </Col> */}

            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Received By <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={userOptions}
                  getOptionLabel={(option: any) =>
                    option?.first_name && option?.last_name
                      ? `${option.first_name} ${option.last_name}`
                      : option?.username || option?.email || ""
                  }
                  value={
                    userOptions.find(
                      (opt: any) =>
                        opt.user_id === formik.values.received_by_user_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "received_by_user_id",
                      selectedOption?.user_id || ""
                    );
                    formik.setFieldValue(
                      "received_by",
                      selectedOption?.first_name && selectedOption?.last_name
                        ? `${selectedOption.first_name} ${selectedOption.last_name}`
                        : selectedOption?.username || ""
                    );
                  }}
                  onBlur={() =>
                    formik.setFieldTouched("received_by_user_id", true)
                  }
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select User"
                      name="received_by"
                      fullWidth
                      size="small"
                      error={
                        formik.touched.received_by_user_id &&
                        Boolean(formik.errors.received_by_user_id)
                      }
                      helperText={
                        formik.touched.received_by_user_id &&
                        typeof formik.errors.received_by_user_id === "string"
                          ? formik.errors.received_by_user_id
                          : undefined
                      }
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor:
                              formik.touched.received_by_user_id &&
                              formik.errors.received_by_user_id
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor:
                              formik.touched.received_by_user_id &&
                              formik.errors.received_by_user_id
                                ? "#dc3545"
                                : "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor:
                              formik.touched.received_by_user_id &&
                              formik.errors.received_by_user_id
                                ? "#dc3545"
                                : "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        opt?.first_name && opt?.last_name
                          ? `${opt.first_name} ${opt.last_name}`
                          : opt?.username || opt?.email || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor:
                          formik.touched.received_by_user_id &&
                          formik.errors.received_by_user_id
                            ? "#dc3545"
                            : "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor:
                          formik.touched.received_by_user_id &&
                          formik.errors.received_by_user_id
                            ? "#dc3545"
                            : "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor:
                          formik.touched.received_by_user_id &&
                          formik.errors.received_by_user_id
                            ? "#dc3545"
                            : "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            {/* Remark */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>

            {/* Document */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Upload document
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>

                {/* Custom styled box */}
                <div
                  className={`d-flex align-items-center justify-content-between p-2 border rounded ${
                    formik.errors.upload_document &&
                    formik.touched.upload_document
                      ? "border-danger"
                      : ""
                  }`}
                  style={{
                    height: "40px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: "#fff",
                    borderColor:
                      formik.errors.upload_document &&
                      formik.touched.upload_document
                        ? "#dc3545"
                        : "#ced4da",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#80bdff";
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#ced4da";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                  onClick={() => {
                    document.getElementById("uploadDocInput")?.click();
                  }}
                >
                  <span
                    className="text-muted"
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: "8px",
                      minWidth: 0,
                    }}
                    title={
                      formik.values.upload_document instanceof File
                        ? formik.values.upload_document.name
                        : formik.values.existing_document_file
                        ? formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        : "Click to upload document"
                    }
                  >
                    {formik.values.upload_document instanceof File ? (
                      formik.values.upload_document.name
                    ) : formik.values.existing_document_file ? (
                      <a
                        href={formik.values.existing_document_file}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-decoration-none text-primary"
                        style={{
                          cursor: "pointer",
                          display: "inline-block",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={
                          formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        }
                      >
                        {formik.values.existing_document_file
                          .split("/")
                          .pop() || "Existing Document"}
                      </a>
                    ) : (
                      "Click to upload document"
                    )}
                  </span>

                  {/* Action buttons */}
                  <div
                    className="d-flex align-items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                  >
                    {formik.values.upload_document instanceof File ? (
                      <>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : formik.values.existing_document_file ? (
                      <>
                        <a
                          href={formik.values.existing_document_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="View document"
                        >
                          <i
                            className="bi bi-eye text-primary"
                            style={{
                              cursor: "pointer",
                              fontSize: "1rem",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                        </a>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : (
                      <i
                        className="bi bi-upload"
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          color: "#6c757d",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#007bff";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#6c757d";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                    )}
                  </div>
                </div>

                {/* Hidden real file input */}
                <Form.Control
                  id="uploadDocInput"
                  type="file"
                  name="upload_document"
                  style={{ display: "none" }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.currentTarget.files?.[0] || null;
                    formik.setFieldValue("upload_document", file);
                    // Clear existing_document_file when a new file is uploaded
                    if (file) {
                      formik.setFieldValue("existing_document_file", null);
                    }
                    formik.setFieldTouched("upload_document", true);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.upload_document &&
                    formik.touched.upload_document
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.upload_document}
                </Form.Control.Feedback>
              </div>
            </Col>
          </Row>

          <hr />

          <Row className="align-items-center mb-4">
            <Col>
              <h6 className="card-title mb-0">Items Details</h6>
            </Col>
          </Row>

          <style>
            {`
              .sticky-column {
                position: relative;
              }
              .sticky-column::before {
                content: '';
                position: absolute;
                left: 0;
                top: 0;
                bottom: 0;
                width: 1px;
                background-color: #dee2e6;
                z-index: 12;
              }
              thead .sticky-column::before {
                z-index: 13;
              }
            `}
          </style>
          <Table hover responsive className="align-middle text-center">
            <thead>
              <tr>
                <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>
                  Sr No.
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Category
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Sub Category
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
                  Item Name
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
                  Item Code
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
                  Unit
                </th>
                {/* <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Order Quantity
                </th> */}
                <th style={{ ...tableStyles.headerAlt, minWidth: "180px" }}>
                  Dispatched Quantity
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "180px" }}>
                  Return Quantity
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Rate
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Amount
                </th>
                <th
                  style={{
                    ...tableStyles.headerAlt,
                    minWidth: "200px",
                    maxWidth: "250px",
                  }}
                >
                  Remarks
                </th>
              </tr>
            </thead>

            <tbody>
              {formik?.values?.grn_items?.map((row: any, idx: number) => {
                return (
                  <tr key={idx}>
                    <td style={tableStyles.cell}>{idx + 1}</td>

                    {/* Category */}
                    <td style={tableStyles.cell}>
                      <span>{row?.category_name}</span>
                    </td>
                    <td style={tableStyles.cell}>
                      <span>{row?.sub_category_name}</span>
                    </td>

                    {/* Item Name */}
                    <td style={tableStyles.cell}>
                      <span>
                        {row?.item_details?.item_name || row?.itemName}
                      </span>
                    </td>

                    {/* Item Code */}
                    <td style={tableStyles.cell}>
                      <span>
                        {row?.item_details?.item_code || row?.itemCode || ""}
                      </span>
                    </td>
                    {/* Item Unit */}
                    <td style={tableStyles.cell}>
                      <span>{row.unit_name || row.unit || ""}</span>
                    </td>

                    <td style={tableStyles.cell}>
                      <span>{row.dispatch_quantity || 0}</span>
                    </td>
                    {/* <td style={tableStyles.cell}>
                      <TextField
                        size="small"
                        type="text"
                        disabled
                        placeholder="Enter dispatch quantity"
                        value={row?.dispatch_quantity !== undefined && row?.dispatch_quantity !== null ? String(row.dispatch_quantity) : ""}
                        onChange={(e) => {
                          const validatedValue = handleNumberInput(e);
                          if (validatedValue !== false) {
                            handleItemFieldChange(idx, 'dispatch_quantity', validatedValue);
                          }
                        }}
                        onBlur={(e) => {
                          const numValue = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                          handleItemFieldChange(idx, 'dispatch_quantity', numValue);
                          formik.setFieldTouched(`grn_items.${idx}.dispatch_quantity`, true);
                        }}
                        inputProps={{
                          pattern: "[0-9]*\\.?[0-9]*",
                          inputMode: "numeric"
                        }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    </td> */}

                    <td style={tableStyles.cell}>
                      <TextField
                        size="small"
                        type="text"
                        placeholder="Enter return quantity"
                        value={
                          row?.return_quantity !== undefined &&
                          row?.return_quantity !== null
                            ? String(row.return_quantity)
                            : row?.quantity !== undefined &&
                              row?.quantity !== null
                            ? String(row.quantity)
                            : ""
                        }
                        onChange={(e) => {
                          const validatedValue = handleNumberInput(e);
                          if (validatedValue !== false) {
                            handleItemFieldChange(
                              idx,
                              "return_quantity",
                              validatedValue
                            );
                          }
                        }}
                        onBlur={(e) => {
                          formik.setFieldTouched(
                            `grn_items.${idx}.return_quantity`,
                            true
                          );
                        }}
                        error={
                          Boolean(
                            Array.isArray(formik.touched.grn_items) &&
                              formik.touched.grn_items[idx] &&
                              (formik.touched.grn_items[idx] as any)
                                ?.return_quantity
                          ) &&
                          (parseFloat(
                            row?.return_quantity || row?.quantity || 0
                          ) <= 0 ||
                            parseFloat(
                              row?.return_quantity || row?.quantity || 0
                            ) > parseFloat(row?.dispatch_quantity || 0))
                        }
                        helperText={
                          Boolean(
                            Array.isArray(formik.touched.grn_items) &&
                              formik.touched.grn_items[idx] &&
                              (formik.touched.grn_items[idx] as any)
                                ?.return_quantity
                          )
                            ? parseFloat(
                                row?.return_quantity || row?.quantity || 0
                              ) <= 0
                              ? "Return quantity must be greater than zero"
                              : parseFloat(
                                  row?.return_quantity || row?.quantity || 0
                                ) > parseFloat(row?.dispatch_quantity || 0)
                              ? "Return quantity cannot be greater than dispatched quantity"
                              : undefined
                            : undefined
                        }
                        inputProps={{
                          pattern: "[0-9]*\\.?[0-9]*",
                          inputMode: "numeric",
                        }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    </td>
                    <td style={tableStyles.cell}>
                      <TextField
                        size="small"
                        type="text"
                        placeholder="Enter rate"
                        value={
                          row?.rate !== undefined && row?.rate !== null
                            ? String(row.rate)
                            : ""
                        }
                        onChange={(e) => {
                          const validatedValue = handleNumberInput(e);
                          if (validatedValue !== false) {
                            handleItemFieldChange(idx, "rate", validatedValue);
                          }
                        }}
                        onBlur={(e) => {
                          const numValue =
                            e.target.value === ""
                              ? 0
                              : parseFloat(e.target.value) || 0;
                          handleItemFieldChange(idx, "rate", numValue);
                          formik.setFieldTouched(`grn_items.${idx}.rate`, true);
                        }}
                        inputProps={{
                          pattern: "[0-9]*\\.?[0-9]*",
                          inputMode: "numeric",
                        }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    </td>
                    <td style={tableStyles.cell}>
                      <span>{row?.amount || 0}</span>
                    </td>

                    {/* Remarks */}
                    <td
                      style={{
                        ...tableStyles.cell,
                        maxWidth: "250px",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                      }}
                    >
                      <TextField
                        size="small"
                        type="text"
                        placeholder="Enter remark"
                        value={row?.remarks || row?.remark || ""}
                        onChange={(e) => {
                          handleItemFieldChange(idx, "remarks", e.target.value);
                        }}
                        onBlur={() => {
                          formik.setFieldTouched(
                            `grn_items.${idx}.remarks`,
                            true
                          );
                        }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton
              label="Cancel"
              onClick={() => {
                showConfirm(
                  "Any unsaved changes will be lost.",
                  "Are you sure?",
                  "Yes",
                  "No"
                ).then((result) => {
                  if (result.isConfirmed) {
                    navigate("/warehouse-item-inward");
                  }
                });
              }}
            />

            <SubmitButton
              isSubmitting={formik.isSubmitting}
              labels={{
                create: isEdit ? "Update GRN" : "Create GRN",
                submitting: isEdit ? "Updating GRN" : "Creating GRN",
              }}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default EditSalesOrderReturnGNR;
